import moment from "moment/moment";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useDebounce } from "use-debounce";
import {
  FiCheckCircle,
  FiExternalLink,
  FiFileText,
  FiMessageSquare,
  FiMoreVertical,
  FiSearch,
  FiSend,
  FiSlash,
  FiTrash2,
  FiX,
} from "react-icons/fi";
import Layout from "../../components/layouts/Layout";
import {
  TablePagination,
} from "../../components/table";
import {
  addSmsConversationNote,
  deleteSmsConversation,
  fetchAutomationConversationEvents,
  fetchConversationEvent,
  fetchSmsConversation,
  fetchSmsConversationMessages,
  fetchSmsConversations,
  forceOptOutSmsConversation,
  markSmsConversationRead,
  replyToSmsConversation,
  SMS_NOTIFICATIONS_UPDATED_EVENT,
  updateSmsConversationStatus,
} from "./AutomationTemplates/automationTemplateApi";
import SmsAccessGuard from "./Components/SmsAccessGuard";
import { normalisePaginatedCollection, paginateCollection } from "./AutomationTemplates/paginatedCollection";
import "./style/sms-billing.css";

const DEFAULT_PAGE_SIZE = 5;

const getApiErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

const readFirstDefined = (source, keys) => {
  if (!source) {
    return null;
  }

  const key = keys.find((item) => source[item] !== undefined && source[item] !== null);

  return key ? source[key] : null;
};

const asArray = (value) => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value;
  }

  if (Array.isArray(value.data)) {
    return value.data;
  }

  return [];
};

const normaliseEvent = (event) => ({
  id: readFirstDefined(event, ["id", "event_id", "eventId", "uuid"]),
  name: readFirstDefined(event, ["event_name", "eventName", "name", "title"]),
});

const normaliseEventsResponse = (payload) => {
  const source = payload?.data || payload || {};
  const collection =
    source.event ||
    source.events ||
    source.live_events ||
    source.liveEvents ||
    source.data ||
    (Array.isArray(source) ? source : []);

  return asArray(collection).map(normaliseEvent).filter((event) => event.id && event.name);
};

const normaliseNote = (note, index) => {
  const author = readFirstDefined(note, ["author", "user", "created_by_user", "createdByUser"]) || {};

  return {
    ...note,
    id: readFirstDefined(note, ["id", "note_id", "noteId", "uuid"]) || index,
    text: readFirstDefined(note, ["note", "body", "text", "content"]),
    authorName:
      readFirstDefined(note, ["author_name", "authorName", "user_name", "userName", "created_by_name"]) ||
      readFirstDefined(author, ["name", "full_name", "fullName"]),
    createdAt: readFirstDefined(note, ["created_at", "createdAt", "timestamp"]),
  };
};

const formatDateTime = (date) => {
  if (!date) {
    return "Not available";
  }

  const parsedDate = moment(date);

  return parsedDate.isValid() ? parsedDate.format("MMM DD, YYYY h:mm A") : "Not available";
};

const formatShortTime = (date) => {
  if (!date) {
    return "";
  }

  const parsedDate = moment(date);

  return parsedDate.isValid() ? parsedDate.format("h:mm A") : "";
};

const formatListTime = (date) => {
  if (!date) return "—";
  const parsedDate = moment(date);
  if (!parsedDate.isValid()) return "—";
  return parsedDate.isSame(moment(), "day") ? parsedDate.format("h:mm A") : parsedDate.format("MMM D");
};

const normaliseStatus = (status, unreadCount) => {
  if (unreadCount > 0) {
    return "awaiting_reply";
  }

  if (status === true || status === 1 || status === "1") {
    return "open";
  }

  if (status === false || status === 0 || status === "0") {
    return "closed";
  }

  const normalisedStatus = String(status || "open").toLowerCase();

  if (["awaiting", "pending_reply", "pending"].includes(normalisedStatus)) {
    return "awaiting_reply";
  }

  return normalisedStatus;
};

const formatStatus = (status) => {
  const statuses = {
    open: "Open",
    closed: "Closed",
    awaiting_reply: "Awaiting Reply",
    awaiting: "Awaiting Reply",
    pending_reply: "Awaiting Reply",
  };

  return statuses[status] || "Open";
};

const normaliseMessageDirection = (message) => {
  const rawDirection = String(
    readFirstDefined(message, ["direction", "message_direction", "type", "sms_type"]) || ""
  ).toLowerCase();

  if (["out", "outbound", "sent", "venue", "system"].includes(rawDirection)) {
    return "out";
  }

  if (["in", "inbound", "received", "customer"].includes(rawDirection)) {
    return "in";
  }

  const isInbound = readFirstDefined(message, ["is_inbound", "isInbound", "incoming"]);

  return isInbound === true || isInbound === 1 || isInbound === "1" ? "in" : "out";
};

const normaliseMessage = (message, index) => {
  const eventType = readFirstDefined(message, ["event_type", "eventType", "activity_type", "activityType"]);
  const text = readFirstDefined(message, [
    "body",
    "message",
    "message_body",
    "messageBody",
    "text",
    "content",
    "description",
  ]);

  return {
    ...message,
    id: readFirstDefined(message, ["id", "message_id", "messageId", "uuid"]) || index,
    direction: normaliseMessageDirection(message),
    text: text || eventType || "No message content available.",
    status: readFirstDefined(message, [
      "delivery_status",
      "deliveryStatus",
      "status",
      "message_status",
      "messageStatus",
    ]),
    createdAt: readFirstDefined(message, [
      "created_at",
      "createdAt",
      "sent_at",
      "sentAt",
      "received_at",
      "receivedAt",
      "delivered_at",
      "deliveredAt",
      "timestamp",
    ]),
    eventType,
    action: readFirstDefined(message, ["action", "automation_action", "automationAction"]),
  };
};

const normaliseConversation = (conversation) => {
  const customer = readFirstDefined(conversation, ["customer", "contact", "guest", "lead"]) || {};
  const event = readFirstDefined(conversation, ["event", "event_details", "eventDetails"]) || {};
  const flow = readFirstDefined(conversation, ["flow", "automation_flow", "automationFlow"]) || {};
  const flowEvent = readFirstDefined(flow, ["event", "event_details", "eventDetails"]) || {};
  const rawMessages = asArray(
    readFirstDefined(conversation, ["messages", "sms_messages", "smsMessages", "timeline", "activities"])
  );
  const unreadCount = Number(
    readFirstDefined(conversation, ["unread_count", "unreadCount", "unread_messages", "unreadMessages"]) || 0
  );
  const phone =
    readFirstDefined(conversation, ["customer_phone", "customerPhone", "phone", "phone_number", "phoneNumber", "mobile"]) ||
    readFirstDefined(customer, ["phone", "phone_number", "phoneNumber", "mobile"]);
  const customerName =
    readFirstDefined(conversation, ["customer_name", "customerName", "contact_name", "contactName", "name"]) ||
    readFirstDefined(customer, ["name", "full_name", "fullName", "customer_name", "customerName"]);
  const eventName =
    readFirstDefined(conversation, ["event_name", "eventName"]) ||
    readFirstDefined(event, ["name", "event_name", "eventName", "title"]) ||
    readFirstDefined(flow, ["event_name", "eventName"]) ||
    readFirstDefined(flowEvent, ["name", "event_name", "eventName", "title"]);
  const flowName =
    readFirstDefined(conversation, ["automation_flow_name", "automationFlowName", "flow_name", "flowName"]) ||
    readFirstDefined(flow, ["name", "flow_name", "flowName", "title"]);
  const messages = rawMessages.map(normaliseMessage);
  const latestMessage = readFirstDefined(conversation, ["latest_message", "latestMessage"]);
  const latestMessagePreview = latestMessage ? normaliseMessage(latestMessage, 0).text : null;
  const flowEventId =
    readFirstDefined(flow, ["event_id", "eventId"]) ||
    readFirstDefined(flowEvent, ["id", "event_id", "eventId"]);
  const rawNotes = asArray(
    readFirstDefined(conversation, ["notes", "venue_notes", "venueNotes", "customer_notes", "customerNotes"])
  );
  const optedOutValue = readFirstDefined(conversation, [
    "is_opted_out",
    "isOptedOut",
    "opted_out",
    "optedOut",
    "force_opted_out",
    "forceOptedOut",
  ]);

  return {
    ...conversation,
    id:
      readFirstDefined(conversation, [
        "id",
        "conversation_id",
        "conversationId",
        "thread_id",
        "threadId",
        "uuid",
      ]) || phone,
    customerName,
    phone,
    eventId:
      readFirstDefined(conversation, ["event_id", "eventId"]) ||
      readFirstDefined(event, ["id", "event_id", "eventId"]) ||
      flowEventId,
    eventName,
    flowName,
    status: normaliseStatus(
      readFirstDefined(conversation, ["status", "state", "conversation_status", "conversationStatus"]),
      unreadCount
    ),
    unreadCount,
    messageCount:
      readFirstDefined(conversation, [
        "message_count",
        "messageCount",
        "total_messages",
        "totalMessages",
        "messages_count",
        "messagesCount",
      ]) ||
      messages.length ||
      (latestMessage ? 1 : 0),
    lastPreview:
      readFirstDefined(conversation, [
        "last_message",
        "lastMessage",
        "last_message_preview",
        "lastMessagePreview",
        "preview",
      ]) ||
      latestMessagePreview ||
      messages[messages.length - 1]?.text,
    lastActivityAt: readFirstDefined(conversation, [
      "last_activity_at",
      "lastActivityAt",
      "last_message_at",
      "lastMessageAt",
      "updated_at",
      "updatedAt",
    ]),
    createdAt: readFirstDefined(conversation, ["created_at", "createdAt"]),
    lastReplyAt: readFirstDefined(conversation, [
      "last_reply_at",
      "lastReplyAt",
      "last_inbound_at",
      "lastInboundAt",
      "last_message_at",
      "lastMessageAt",
    ]),
    isOptedOut:
      optedOutValue === true || optedOutValue === 1 || optedOutValue === "1" || optedOutValue === "true",
    optOutReason: readFirstDefined(conversation, ["opt_out_reason", "optOutReason"]),
    optedOutAt: readFirstDefined(conversation, ["opted_out_at", "optedOutAt"]),
    notes: rawNotes.map(normaliseNote),
    messages,
  };
};

const normaliseConversationsResponse = (payload) => {
  const collection = normalisePaginatedCollection(
    payload,
    ["conversations", "sms_conversations", "smsConversations"],
    DEFAULT_PAGE_SIZE
  );

  return {
    ...collection,
    data: collection.data.map(normaliseConversation),
  };
};

const normaliseConversationResponse = (payload) => {
  const source = payload?.data || payload || {};
  const conversation =
    source.conversation ||
    source.sms_conversation ||
    source.smsConversation ||
    source.data ||
    source;

  return normaliseConversation(conversation);
};

const fetchMissingEventName = async (eventId, eventName) => {
  if (eventName || !eventId) {
    return eventName;
  }

  try {
    const response = await fetchConversationEvent(eventId);
    const source = response.data?.data || response.data || {};
    const event = source.event || source.event_details || source.eventDetails || source;

    return readFirstDefined(event, ["event_name", "eventName", "name", "title"]);
  } catch (error) {
    return null;
  }
};

const normaliseMessagesResponse = (payload) => {
  const source = payload?.data || payload || {};
  const collection =
    source.messages ||
    source.sms_messages ||
    source.smsMessages ||
    source.data ||
    (Array.isArray(source) ? source : []);

  return asArray(collection).map(normaliseMessage);
};

function ConversationView() {
  const { eventId } = useParams();
  const location = useLocation();
  const eventDetails = location?.state || {};
  const isEventScoped = Boolean(eventId);

  const [conversationResults, setConversationResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchKeyWord, setSearchKeyWord] = useState("");
  const [searchKeyWordDebounce] = useDebounce(searchKeyWord, 600);
  const [statusFilter, setStatusFilter] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [eventFilter, setEventFilter] = useState("");
  const [filterEvents, setFilterEvents] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [replySending, setReplySending] = useState(false);
  const [conversationActionLoading, setConversationActionLoading] = useState(false);
  const [optOutModalOpen, setOptOutModalOpen] = useState(false);
  const [optOutReason, setOptOutReason] = useState("");
  const [noteBody, setNoteBody] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [detailActionMessage, setDetailActionMessage] = useState(null);
  const [apiAvailable, setApiAvailable] = useState(true);
  const selectedConversationIdRef = useRef(null);
  const conversations = useMemo(
    () => paginateCollection(conversationResults, currentPage, DEFAULT_PAGE_SIZE),
    [conversationResults, currentPage]
  );

  const eventOptions = useMemo(() => {
    const seen = {};

    const conversationEvents = conversationResults
      .filter((item) => item.eventId && item.eventName)
      .map((item) => ({ id: item.eventId, name: item.eventName }));

    return [...filterEvents, ...conversationEvents].filter((item) => {
        const key = String(item.id);
        if (seen[key]) {
          return false;
        }

        seen[key] = true;
        return true;
      });
  }, [conversationResults, filterEvents]);

  const selectedDisplayName =
    selectedConversation?.customerName || selectedConversation?.phone || "Conversation";

  useEffect(() => {
    selectedConversationIdRef.current = selectedConversationId;
  }, [selectedConversationId]);

  useEffect(() => {
    if (isEventScoped) {
      return undefined;
    }

    let mounted = true;

    fetchAutomationConversationEvents({
      page: 1,
      per_page: 1000,
      search: "",
    })
      .then((response) => {
        if (mounted) {
          setFilterEvents(normaliseEventsResponse(response.data));
        }
      })
      .catch(() => {
        if (mounted) {
          setFilterEvents([]);
        }
      });

    return () => {
      mounted = false;
    };
  }, [isEventScoped]);

  const handleSearch = (query) => {
    setSearchKeyWord(query);
    setCurrentPage(1);
  };

  const loadConversations = useCallback(async (isMounted = () => true) => {
    try {
      setLoading(true);
      const params = {
        per_page: 1000,
      };

      if (searchKeyWordDebounce) {
        params.search = searchKeyWordDebounce;
      }

      if (isEventScoped) {
        params.event_id = eventId;
      } else if (eventFilter) {
        params.event_id = eventFilter;
      }

      if (statusFilter) {
        params.status = statusFilter;
      }

      if (unreadOnly) {
        params.unread_only = 1;
      }

      const response = await fetchSmsConversations(params);
      const nextConversations = normaliseConversationsResponse(response.data);

      if (!isMounted()) {
        return;
      }

      setConversationResults(nextConversations.data);
      setApiAvailable(true);

      const selectedStillExists = nextConversations.data.some(
        (item) => item.id === selectedConversationIdRef.current
      );
      const nextSelected =
        (selectedStillExists &&
          nextConversations.data.find((item) => item.id === selectedConversationIdRef.current)) ||
        nextConversations.data[0] ||
        null;

      setSelectedConversationId(nextSelected?.id || null);
      setSelectedConversation(nextSelected);
    } catch (error) {
      if (isMounted()) {
        setConversationResults([]);
        setSelectedConversationId(null);
        setSelectedConversation(null);
        setApiAvailable(false);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [
    eventFilter,
    eventId,
    isEventScoped,
    searchKeyWordDebounce,
    statusFilter,
    unreadOnly,
  ]);

  useEffect(() => {
    let mounted = true;

    loadConversations(() => mounted);

    return () => {
      mounted = false;
    };
  }, [loadConversations]);

  useEffect(() => {
    if (!selectedConversationId) {
      return undefined;
    }

    let mounted = true;

    const loadConversationDetail = async () => {
      try {
        setDetailLoading(true);
        const [conversationResponse, messagesResponse] = await Promise.all([
          fetchSmsConversation(selectedConversationId, {
            event_id: isEventScoped ? eventId : undefined,
          }),
          fetchSmsConversationMessages(selectedConversationId, {
            order: "asc",
            per_page: 50,
          }),
        ]);
        const listConversation = conversations.data.find((item) => item.id === selectedConversationId);
        const normalisedDetail = normaliseConversationResponse(conversationResponse.data);
        const resolvedEventId = normalisedDetail.eventId || listConversation?.eventId;
        const resolvedEventName = await fetchMissingEventName(
          resolvedEventId,
          normalisedDetail.eventName || listConversation?.eventName
        );
        const detail = {
          ...normalisedDetail,
          eventId: resolvedEventId,
          eventName: resolvedEventName,
          messages: normaliseMessagesResponse(messagesResponse.data),
        };

        if (mounted) {
          setSelectedConversation(detail);

          if (detail.unreadCount > 0) {
            markSmsConversationRead(selectedConversationId)
              .then(() => window.dispatchEvent(new Event(SMS_NOTIFICATIONS_UPDATED_EVENT)))
              .catch(() => {});
            setSelectedConversation((previous) =>
              previous ? { ...previous, unreadCount: 0, status: normaliseStatus(previous.status, 0) } : previous
            );
            setConversationResults((previous) =>
              previous.map((item) => {
                if (item.id !== selectedConversationId || item.unreadCount === 0) {
                  return item;
                }

                return { ...item, unreadCount: 0, status: normaliseStatus(item.status, 0) };
              })
            );
          }
        }
      } catch (error) {
        if (mounted) {
          const fallback = conversations.data.find((item) => item.id === selectedConversationId);

          setSelectedConversation(fallback || null);
        }
      } finally {
        if (mounted) {
          setDetailLoading(false);
        }
      }
    };

    loadConversationDetail();

    return () => {
      mounted = false;
    };
  }, [conversations.data, eventId, isEventScoped, selectedConversationId]);

  const handleSelectConversation = (conversation) => {
    setSelectedConversationId(conversation.id);
    setSelectedConversation(conversation);
    setReplyBody("");
    setNoteBody("");
    setDetailActionMessage(null);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setCurrentPage(1);
  };

  const handleUnreadFilterChange = (event) => {
    setUnreadOnly(event.target.checked);
    setCurrentPage(1);
  };

  const handleEventFilterChange = (event) => {
    setEventFilter(event.target.value);
    setCurrentPage(1);
  };

  const handleConversationPageChange = (page) => {
    const nextPage = Number(page) || 1;
    const nextConversation = conversationResults[(nextPage - 1) * DEFAULT_PAGE_SIZE] || null;

    setCurrentPage(nextPage);
    setSelectedConversationId(nextConversation?.id || null);
    setSelectedConversation(nextConversation);
    setReplyBody("");
  };

  const renderMessageMeta = (message) => {
    const meta = [
      formatShortTime(message.createdAt),
      message.status,
      message.action,
      message.eventType,
    ].filter(Boolean);

    return meta.length ? meta.join(" - ") : "Not available";
  };

  const refreshSelectedConversation = async () => {
    if (!selectedConversationId) {
      return;
    }

    const [conversationResponse, messagesResponse] = await Promise.all([
      fetchSmsConversation(selectedConversationId, {
        event_id: isEventScoped ? eventId : undefined,
      }),
      fetchSmsConversationMessages(selectedConversationId, {
        order: "asc",
        per_page: 50,
      }),
    ]);

    const normalisedDetail = normaliseConversationResponse(conversationResponse.data);
    const resolvedEventId = normalisedDetail.eventId || selectedConversation?.eventId;
    const resolvedEventName = await fetchMissingEventName(
      resolvedEventId,
      normalisedDetail.eventName || selectedConversation?.eventName
    );
    const detail = {
      ...normalisedDetail,
      eventId: resolvedEventId,
      eventName: resolvedEventName,
      messages: normaliseMessagesResponse(messagesResponse.data),
    };

    setSelectedConversation(detail);
  };

  const handleSendReply = async () => {
    const message = replyBody.trim();

    if (!selectedConversationId || !message) {
      return;
    }

    try {
      setReplySending(true);
      await replyToSmsConversation(selectedConversationId, { message, message_body: message });
      setReplyBody("");
      await refreshSelectedConversation();
      await loadConversations();
    } finally {
      setReplySending(false);
    }
  };

  const handleStatusChange = async (event) => {
    const nextStatus = event.target.value;

    if (!selectedConversationId) {
      return;
    }

    try {
      setConversationActionLoading(true);
      await updateSmsConversationStatus(selectedConversationId, { status: nextStatus });
      setSelectedConversation((previous) =>
        previous ? { ...previous, status: normaliseStatus(nextStatus, 0) } : previous
      );
      setConversationResults((previous) =>
        previous.map((item) =>
          item.id === selectedConversationId
            ? { ...item, status: normaliseStatus(nextStatus, 0) }
            : item
        )
      );
    } finally {
      setConversationActionLoading(false);
    }
  };

  const handleDeleteConversation = async () => {
    if (!selectedConversationId || !window.confirm("Delete this conversation?")) {
      return;
    }

    try {
      setConversationActionLoading(true);
      await deleteSmsConversation(selectedConversationId);
      await loadConversations();
    } finally {
      setConversationActionLoading(false);
    }
  };

  const handleForceOptOut = async () => {
    const reason = optOutReason.trim();

    if (!selectedConversationId || !reason) {
      return;
    }

    try {
      setConversationActionLoading(true);
      setDetailActionMessage(null);
      await forceOptOutSmsConversation(selectedConversationId, { reason });
      setOptOutModalOpen(false);
      setOptOutReason("");
      setDetailActionMessage({ type: "success", text: "Customer successfully opted out." });
      await refreshSelectedConversation();
      await loadConversations();
    } catch (error) {
      setDetailActionMessage({
        type: "error",
        text: getApiErrorMessage(error, "Unable to opt this customer out."),
      });
    } finally {
      setConversationActionLoading(false);
    }
  };

  const handleAddNote = async () => {
    const note = noteBody.trim();

    if (!selectedConversationId || !note) {
      return;
    }

    try {
      setNoteSaving(true);
      setDetailActionMessage(null);
      await addSmsConversationNote(selectedConversationId, { note });
      setNoteBody("");
      setDetailActionMessage({ type: "success", text: "Note saved." });
      await refreshSelectedConversation();
    } catch (error) {
      setDetailActionMessage({
        type: "error",
        text: getApiErrorMessage(error, "Unable to save this note."),
      });
    } finally {
      setNoteSaving(false);
    }
  };

  const tabs = [
    ["Overview", "/conversions"],
    ["Conversations", "/conversions/conversations", true],
    ["Notifications", "/conversions/notifications"],
    ["Analytics", "/conversions/analytics"],
    ["My Automation Flows", "/conversions/automation-flows"],
    ["Browse Templates", "/conversions/sms-automation-templates"],
    ["Billing", "/conversions/billing"],
  ];

  const handleMarkClosed = () => handleStatusChange({ target: { value: "closed" } });

  return (
    <SmsAccessGuard>
      <Layout>
        <main className="page conversations-inbox-page">
          <header className="conversations-inbox-title">
            <h1>Conversations</h1>
            <p>
              {isEventScoped
                ? `Manage conversations for ${eventDetails.event_name || "this event"}.`
                : "Manage and respond to all 2-way SMS conversations."}
            </p>
          </header>

          <nav className="conversations-tabs conversations-inbox-tabs" aria-label="Conversations sections">
            {tabs.map(([label, path, active]) => (
              <Link key={path} to={path} className={active ? "active" : ""}>{label}</Link>
            ))}
          </nav>

          <section className="conversation-workspace">
            <aside className="conversation-inbox-list-panel">
              <div className="conversation-list-filters">
                <label className="conversation-search-field">
                  <input
                    value={searchKeyWord}
                    onChange={(event) => handleSearch(event.target.value)}
                    placeholder="Search name, phone, or event"
                    aria-label="Search conversations"
                  />
                  <FiSearch />
                </label>
                <div className="conversation-filter-row">
                  <select value={statusFilter} onChange={handleStatusFilterChange} aria-label="Conversation status">
                    <option value="">All Statuses</option>
                    <option value="open">Open</option>
                    <option value="closed">Closed</option>
                    <option value="awaiting_reply">Awaiting Reply</option>
                  </select>
                  {!isEventScoped ? (
                    <select value={eventFilter} onChange={handleEventFilterChange} aria-label="Conversation event">
                      <option value="">All Events</option>
                      {eventOptions.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                    </select>
                  ) : null}
                </div>
                <label className="conversation-unread-only">
                  <input type="checkbox" checked={unreadOnly} onChange={handleUnreadFilterChange} />
                  <span>Unread only</span>
                </label>
              </div>

              <div className="conversation-inbox-items">
                {loading ? <div className="conversation-loading"><span className="spinner-border" /></div> : null}
                {!loading && conversations.data.length ? conversations.data.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    className={`conversation-inbox-item ${item.id === selectedConversationId ? "active" : ""}`}
                    onClick={() => handleSelectConversation(item)}
                  >
                    <span className="inbox-item-heading">
                      <strong>{item.customerName || item.phone || "Unknown Customer"}</strong>
                      {item.unreadCount > 0 ? <span className="unread-dot" /> : null}
                    </span>
                    <small>{item.phone || "No phone available"}</small>
                    <span className="inbox-item-preview">{item.lastPreview || "No messages yet."}</span>
                    <span className="inbox-item-footer">
                      <span>{item.eventName || item.flowName || "No event linked"}</span>
                      <time>{formatListTime(item.lastActivityAt)}</time>
                      <em className={item.status === "awaiting_reply" ? "waiting" : "replied"}>{formatStatus(item.status)}</em>
                    </span>
                  </button>
                )) : null}
                {!loading && !conversations.data.length ? (
                  <div className="conversation-empty-state">{apiAvailable ? "No conversations found." : "Conversation API unavailable right now."}</div>
                ) : null}
              </div>

              {!loading && conversations.total > 0 ? (
                <div className="conversation-pagination conversation-inbox-pagination">
                  <TablePagination
                    currentPage={conversations.currentPage}
                    total={conversations.total}
                    perPage={conversations.perPage}
                    from={conversations.from}
                    to={conversations.to}
                    onChange={handleConversationPageChange}
                    pageRangeDisplayed={5}
                  />
                </div>
              ) : null}
            </aside>

            <section className="conversation-thread-panel">
              {selectedConversation ? (
                <>
                  <header className="conversation-thread-header">
                    <div><h2>{selectedDisplayName}</h2><p>{selectedConversation.phone || "No phone available"}</p></div>
                    <div className="thread-header-actions">
                      <span className={`thread-status ${selectedConversation.status}`}>{formatStatus(selectedConversation.status)}</span>
                      <button type="button" aria-label="More conversation options"><FiMoreVertical /></button>
                    </div>
                  </header>

                  <div className="conversation-thread-body">
                    {selectedConversation.createdAt ? <div className="conversation-date-divider"><span>{moment(selectedConversation.createdAt).format("MMM D, YYYY")}</span></div> : null}
                    {detailLoading ? <div className="conversation-loading"><span className="spinner-border" /></div> : null}
                    {!detailLoading && selectedConversation.messages.length ? selectedConversation.messages.map((message) => (
                      <div key={message.id} className={`conversation-message ${message.direction === "out" ? "outgoing" : "incoming"}`}>
                        <p>{message.text}</p>
                        <small>{renderMessageMeta(message)}</small>
                      </div>
                    )) : null}
                    {!detailLoading && !selectedConversation.messages.length ? <div className="conversation-empty-state">No message timeline available.</div> : null}
                  </div>

                  <footer className="conversation-composer">
                    <div className="composer-tools">
                      <button type="button" className="active"><FiMessageSquare /> Reply</button>
                    </div>
                    <div className="composer-input-wrap">
                      <textarea
                        rows={4}
                        maxLength={160}
                        value={replyBody}
                        onChange={(event) => setReplyBody(event.target.value)}
                        disabled={replySending || conversationActionLoading || selectedConversation.isOptedOut}
                        placeholder={selectedConversation.isOptedOut ? "This customer has opted out." : "Type your message..."}
                      />
                      <span className="composer-count">{replyBody.length} / 160</span>
                      <button
                        className="composer-send-button"
                        type="button"
                        disabled={!replyBody.trim() || replySending || conversationActionLoading || selectedConversation.isOptedOut}
                        onClick={handleSendReply}
                      >
                        <FiSend /> {replySending ? "Sending" : "Send"}
                      </button>
                    </div>
                  </footer>
                </>
              ) : <div className="conversation-empty-state conversation-select-empty">Select a conversation to view the SMS timeline.</div>}
            </section>

            <aside className="conversation-details-panel">
              <h2>Conversation Details</h2>
              {selectedConversation ? (
                <>
                  <dl>
                    <dt>Customer</dt><dd>{selectedConversation.customerName || "Not available"}</dd>
                    <dt>Phone</dt><dd>{selectedConversation.phone || "Not available"}</dd>
                    <dt>Event</dt><dd className="detail-linked-value">{selectedConversation.eventName || "Not available"}{selectedConversation.eventId ? <FiExternalLink /> : null}</dd>
                    <dt>Automation Flow</dt><dd>{selectedConversation.flowName || "Not available"}</dd>
                    <dt>Current Status</dt>
                    <dd><select value={selectedConversation.status} onChange={handleStatusChange} disabled={conversationActionLoading}>
                      <option value="open">Open</option><option value="closed">Closed</option><option value="awaiting_reply">Awaiting Reply</option>
                    </select></dd>
                    <dt>Created</dt><dd>{formatDateTime(selectedConversation.createdAt)}</dd>
                    <dt>Last Reply</dt><dd>{formatDateTime(selectedConversation.lastReplyAt)}</dd>
                    <dt>Total Messages</dt><dd>{selectedConversation.messageCount || 0}</dd>
                    <dt>SMS Permission</dt><dd>{selectedConversation.isOptedOut ? "Opted out" : "Subscribed"}</dd>
                  </dl>
                  {selectedConversation.isOptedOut ? (
                    <div className="conversation-opt-out-summary">
                      <strong><FiSlash /> Forced opt out</strong>
                      <p>{selectedConversation.optOutReason || "No reason supplied."}</p>
                      {selectedConversation.optedOutAt ? <small>{formatDateTime(selectedConversation.optedOutAt)}</small> : null}
                    </div>
                  ) : null}
                  <section className="conversation-notes-section">
                    <h3><FiFileText /> Venue notes</h3>
                    <div className="conversation-notes-list">
                      {selectedConversation.notes.length ? selectedConversation.notes.map((note) => (
                        <article key={note.id}>
                          <p>{note.text || "Empty note"}</p>
                          <small>{[note.authorName, formatDateTime(note.createdAt)].filter(Boolean).join(" · ")}</small>
                        </article>
                      )) : <p className="conversation-no-notes">No notes added yet.</p>}
                    </div>
                    <textarea
                      rows={3}
                      maxLength={1000}
                      value={noteBody}
                      onChange={(event) => setNoteBody(event.target.value)}
                      placeholder="Add a private venue note..."
                      disabled={noteSaving}
                    />
                    <div className="conversation-note-submit">
                      <span>{noteBody.length} / 1000</span>
                      <button type="button" onClick={handleAddNote} disabled={!noteBody.trim() || noteSaving}>
                        {noteSaving ? "Saving..." : "Save note"}
                      </button>
                    </div>
                  </section>
                  {detailActionMessage ? (
                    <p className={`conversation-detail-message ${detailActionMessage.type}`} role="status">
                      {detailActionMessage.text}
                    </p>
                  ) : null}
                  <div className="conversation-detail-actions">
                    <button type="button" className="mark-closed" onClick={handleMarkClosed} disabled={conversationActionLoading || selectedConversation.status === "closed"}><FiCheckCircle /> Mark as Closed</button>
                    <button type="button" className="force-opt-out" onClick={() => { setOptOutReason(""); setDetailActionMessage(null); setOptOutModalOpen(true); }} disabled={conversationActionLoading || selectedConversation.isOptedOut}><FiSlash /> {selectedConversation.isOptedOut ? "Already Opted Out" : "Force Opt Out"}</button>
                    <button type="button" className="delete-conversation" onClick={handleDeleteConversation} disabled={conversationActionLoading}><FiTrash2 /> Delete Conversation</button>
                  </div>
                </>
              ) : <div className="conversation-empty-state">No conversation selected.</div>}
            </aside>
          </section>
          {optOutModalOpen ? (
            <div className="conversation-modal-overlay" role="presentation" onMouseDown={() => setOptOutModalOpen(false)}>
              <section className="conversation-opt-out-modal" role="dialog" aria-modal="true" aria-labelledby="force-opt-out-title" onMouseDown={(event) => event.stopPropagation()}>
                <header>
                  <div><span><FiSlash /></span><div><h2 id="force-opt-out-title">Force customer opt out</h2><p>This immediately prevents further SMS messages to this number.</p></div></div>
                  <button type="button" aria-label="Close opt-out dialog" onClick={() => setOptOutModalOpen(false)}><FiX /></button>
                </header>
                <label htmlFor="force-opt-out-reason">Reason <strong>*</strong></label>
                <textarea id="force-opt-out-reason" rows={4} maxLength={500} value={optOutReason} onChange={(event) => setOptOutReason(event.target.value)} placeholder="Explain why this customer is being manually opted out..." autoFocus />
                <div className="conversation-opt-out-modal-footer">
                  <span>{optOutReason.length} / 500</span>
                  <div><button type="button" className="cancel" onClick={() => setOptOutModalOpen(false)}>Cancel</button><button type="button" className="confirm" onClick={handleForceOptOut} disabled={!optOutReason.trim() || conversationActionLoading}>{conversationActionLoading ? "Submitting..." : "Confirm opt out"}</button></div>
                </div>
              </section>
            </div>
          ) : null}
        </main>
      </Layout>
    </SmsAccessGuard>
  );
}

export default ConversationView;
